import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { GlassCard } from '@/components/ui/GlassCard';
import type { OutputPeriodBucket } from '@/lib/api';
import { CATEGORY_DISPLAY, CATEGORY_ORDER } from './categories';

interface CategoryMixChartProps {
  buckets: OutputPeriodBucket[];
  /** 'pt' はPt配分、'count' は件数配分 */
  metric: 'pt' | 'count';
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 期間ごとの目的カテゴリ配分を積み上げ棒で示す。何にPtを使ったかを期間比較する
 * @graph-connects none
 */
export function CategoryMixChart({ buckets, metric }: CategoryMixChartProps) {
  const data = buckets.map((bucket) => {
    const row: Record<string, string | number> = { period: bucket.key };
    for (const category of CATEGORY_ORDER) {
      const found = bucket.byCategory.find((item) => item.category === category);
      row[category] = found ? (metric === 'pt' ? found.totalPt : found.releaseCount) : 0;
    }
    return row;
  });

  return (
    <GlassCard>
      <p className="mb-3 text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
        目的カテゴリ配分（{metric === 'pt' ? 'Pt' : '件数'}）
      </p>
      <div
        role="img"
        aria-label={`目的カテゴリ配分（${metric === 'pt' ? 'Pt' : '件数'}）の期間別積み上げ`}
        style={{ width: '100%', height: 280 }}
      >
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              stroke="var(--color-border)"
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              stroke="var(--color-border)"
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-bg-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 12,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {CATEGORY_ORDER.map((category) => (
              <Bar
                key={category}
                dataKey={category}
                name={CATEGORY_DISPLAY[category].label}
                stackId="mix"
                fill={CATEGORY_DISPLAY[category].color}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </GlassCard>
  );
}
